import {
    Box,
    Breadcrumbs,
    Card,
    CardActionArea,
    CardContent,
    Link,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from '../../components/Loading';
import YoutubeTutorialDialog from '../../components/tutorials/YoutubeTutorialDialog';
import {
    groupTutorialsBySection,
    resolveTutorialPlaybackUrl,
    youtubeThumbnailUrl,
} from '../../helpers/tutorials';
import { useTutorialVideos } from '../../helpers/useTutorialVideos';

function TutorialCard({ item, onSelect, preferMobile }) {
    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardActionArea onClick={() => onSelect(item)} sx={{ height: '100%' }}>
                <CardContent
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 1.25,
                        '&:last-child': { pb: 1.25 },
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            flexShrink: 0,
                            width: 120,
                            height: 68,
                            borderRadius: 1,
                            overflow: 'hidden',
                            bgcolor: 'grey.900',
                        }}
                    >
                        <Box
                            component="img"
                            src={youtubeThumbnailUrl(resolveTutorialPlaybackUrl(item, preferMobile))}
                            alt=""
                            loading="lazy"
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(0,0,0,0.28)',
                            }}
                        >
                            <PlayCircleOutlineIcon sx={{ color: '#fff', fontSize: 28 }} />
                        </Box>
                    </Box>
                    <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ lineHeight: 1.35, pr: 0.5 }}
                    >
                        {item.name}
                    </Typography>
                </CardContent>
            </CardActionArea>
        </Card>
    );
}

function TutorialsPage() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { videos, loading } = useTutorialVideos({});
    const [selected, setSelected] = useState(null);
    const sections = groupTutorialsBySection(videos);

    if (loading) {
        return <Loading />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                <Link color="inherit" onClick={() => navigate('/admin')} to="/admin">
                    Dashboard
                </Link>
                <Typography color="text.primary">Tutoriais</Typography>
            </Breadcrumbs>

            <Typography variant="h4" fontWeight={700} gutterBottom>
                Tutoriais
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 640 }}>
                Videos tutoriais para que você domine o Acompanha 3D.
            </Typography>

            {sections.length === 0 ? (
                <Typography color="text.secondary">Nenhum tutorial disponível no momento.</Typography>
            ) : null}

            {sections.map((section) => (
                <Box key={section.value} sx={{ mb: 3.5 }}>
                    <Typography
                        variant="overline"
                        sx={{
                            display: 'block',
                            fontWeight: 700,
                            letterSpacing: 1.1,
                            color: 'text.secondary',
                            mb: 1.25,
                        }}
                    >
                        {section.label}
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, minmax(0, 1fr))',
                                lg: 'repeat(2, minmax(0, 420px))',
                            },
                            gap: 1.5,
                        }}
                    >
                        {section.items.map((item) => (
                            <TutorialCard
                                key={item.id}
                                item={item}
                                preferMobile={isMobile}
                                onSelect={setSelected}
                            />
                        ))}
                    </Box>
                </Box>
            ))}

            <YoutubeTutorialDialog
                open={Boolean(selected)}
                onClose={() => setSelected(null)}
                videoUrl={resolveTutorialPlaybackUrl(selected, isMobile)}
                title={selected?.name}
            />
        </div>
    );
}

export default TutorialsPage;
